'use client';

import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { textStyle } from '@/uiPrimitives/typography/text';
import { addTestSelector } from '@dop/shared/helpers/testSelector';
import {
	accordionContentPadding,
	delayIconTransitionHover,
} from './Accordion.css';
import { icon } from '@/uiPrimitives/media/icon';
import { chevronIcon } from '@/uiIcons/chevronIcon.css';
import { Collapse } from '../Collapse';
import { ReactNode, useId } from 'react';
import {
	useAccordionItemOpened,
	useDispatchAccordion,
} from './AccordionProvider.client';
import { A11YAccordionActionLabel } from './A11YAccordionActionLabel';
import { focusBox } from '@/uiPrimitives/base/focus.css';
import { stack } from '@/uiPrimitives/layout/stack';
import { occupy } from '@/uiPrimitives/layout/occupy';

export type AccordionItemProps = {
	className?: string;
	/** Provide your own \<h2\>, \<h3\> or \<h4\> with your own `textBlock` */
	summarySlot: ReactNode;
	children: ReactNode;
	onToggle?: (props: { id: string; opening: boolean }) => void;
};

export const AccordionItem = ({
	className,
	summarySlot,
	children,
	onToggle,
}: AccordionItemProps) => {
	const id = useId();
	const isOpened = useAccordionItemOpened(id);
	const dispatchAccordion = useDispatchAccordion();

	return (
		<div className={classes(className, stack({}))}>
			<button
				aria-controls={id}
				aria-expanded={isOpened}
				onClick={() => {
					dispatchAccordion({
						type: isOpened ? 'close' : 'open',
						id,
					});
					onToggle?.({
						id,
						opening: !isOpened,
					});
				}}
				className={classes(
					focusBox,
					shelf({
						gap: '-1 | small',
						gridTemplateColumns: 'minmax(0, 1fr) auto',
						alignItems: 'start',
					}),
					box({
						paddingBlock: '1 | h3',
						paddingInline: 'negative 1 | h3',
					}),
					textStyle({
						color: 'positiveBlue',
						fontWeight: isOpened ? 'bold' : undefined,
					}),
				)}
				{...addTestSelector(`uiAccordionItem`)}
			>
				{summarySlot}
				<A11YAccordionActionLabel isOpened={isOpened} />
				<span
					className={classes(
						occupy({ alignItems: 'center', blockSize: '0 | p' }),
					)}
				>
					<i
						className={classes(
							delayIconTransitionHover,
							icon({
								icon: chevronIcon,
								adjustIcon: 'rotate90deg',
								flip: isOpened ? 'horizontal:hover' : 'horizontalNone:hover',
								blockSize: '1ex',
							}),
						)}
					/>
				</span>
			</button>
			<Collapse id={id} isOpened={isOpened}>
				<div
					className={classes(accordionContentPadding)}
					{...addTestSelector(`uiAccordionContent`)}
				>
					{children}
				</div>
			</Collapse>
		</div>
	);
};
